// Operaciones que mantienen sincronizados los registros vinculados a un movimiento de dinero
// (pagos de deuda, cobros de préstamos, abonos a compromisos, compras, ventas y gastos fijos).
import { supabase } from '../supabaseClient';
import { parseFecha } from './format';
import { ivaDesdeTotal } from './iva';
import { mismoPeriodoGastoFijo } from './finanzas';

// Ejecuta una consulta de Supabase y lanza el error si falla (en vez de ignorarlo en silencio).
export async function q(consulta) {
  const { data, error } = await consulta;
  if (error) throw error;
  return data;
}

export async function crearMovimiento({ userId, bolsilloId, tipo, monto, descripcion, fecha, gastoFijoId = null, rubro = null, conFactura = false, iva = 0 }) {
  return q(supabase.from('bolsillo_movimientos').insert({
    user_id: userId,
    bolsillo_id: bolsilloId,
    tipo,
    monto,
    descripcion: descripcion || null,
    fecha,
    gasto_fijo_id: gastoFijoId,
    rubro: tipo === 'egreso' ? rubro : null,
    con_factura: tipo === 'egreso' ? !!conFactura : false,
    iva: tipo === 'egreso' && conFactura ? iva : 0,
  }).select().single());
}

export async function borrarMovimientoSimple(movimientoId) {
  if (!movimientoId) return;
  await q(supabase.from('bolsillo_movimientos').delete().eq('id', movimientoId));
}

const suma = (filas, campo = 'monto') => (filas || []).reduce((s, f) => s + Number(f[campo] || 0), 0);

export async function recalcularDeuda(deudaId) {
  const deuda = await q(supabase.from('deudas').select('monto_original').eq('id', deudaId).maybeSingle());
  if (!deuda) return;
  const pagos = await q(supabase.from('pagos_deuda').select('monto').eq('deuda_id', deudaId));
  await q(supabase.from('deudas').update({ activa: suma(pagos) < Number(deuda.monto_original) }).eq('id', deudaId));
}

export async function recalcularPrestamo(prestamoId) {
  const prestamo = await q(supabase.from('prestamos').select('monto').eq('id', prestamoId).maybeSingle());
  if (!prestamo) return;
  const cobros = await q(supabase.from('cobros_prestamo').select('monto').eq('prestamo_id', prestamoId));
  await q(supabase.from('prestamos').update({ cobrado: suma(cobros) >= Number(prestamo.monto) }).eq('id', prestamoId));
}

export async function recalcularCompromiso(compromisoId) {
  const comp = await q(supabase.from('compromisos').select('monto_total').eq('id', compromisoId).maybeSingle());
  if (!comp) return;
  const abonos = await q(supabase.from('abonos').select('monto').eq('compromiso_id', compromisoId));
  const estado = suma(abonos) >= Number(comp.monto_total) ? 'pagado' : 'pendiente';
  await q(supabase.from('compromisos').update({ estado }).eq('id', compromisoId));
}

// monto_actual y completada de la meta se recalculan desde abonos_meta.
export async function recalcularMeta(metaId) {
  const meta = await q(supabase.from('metas').select('monto_objetivo').eq('id', metaId).maybeSingle());
  if (!meta) return;
  const abonos = await q(supabase.from('abonos_meta').select('monto').eq('meta_id', metaId));
  const total = suma(abonos);
  await q(supabase.from('metas').update({
    monto_actual: total,
    completada: total >= Number(meta.monto_objetivo),
  }).eq('id', metaId));
}

async function desmarcarGastoFijo(gastoFijoId, fechaMovimiento) {
  const g = await q(supabase.from('gastos_fijos').select('*').eq('id', gastoFijoId).maybeSingle());
  if (!g || !g.pagado) return;
  const f = parseFecha(fechaMovimiento) || new Date();
  const mismoPeriodo = mismoPeriodoGastoFijo(g, g.mes_pago, g.ano_pago, f);
  if (mismoPeriodo) {
    await q(supabase.from('gastos_fijos').update({ pagado: false, mes_pago: null, ano_pago: null }).eq('id', gastoFijoId));
  }
}

// Qué otros registros dependen de este movimiento (para avisar antes de borrar).
export async function buscarVinculos(movimientoId) {
  const [pagos, cobros, abonos, compras, ventas] = await Promise.all([
    q(supabase.from('pagos_deuda').select('id, deuda_id').eq('movimiento_id', movimientoId)),
    q(supabase.from('cobros_prestamo').select('id, prestamo_id').eq('movimiento_id', movimientoId)),
    q(supabase.from('abonos').select('id, compromiso_id').eq('movimiento_id', movimientoId)),
    q(supabase.from('compras').select('id').eq('movimiento_id', movimientoId)),
    q(supabase.from('ventas').select('id').eq('movimiento_id', movimientoId)),
  ]);
  return { pagos, cobros, abonos, compras, ventas };
}

// Borra un movimiento y, si era un pago de deuda / cobro / abono, también ese registro,
// dejando la deuda, el préstamo o el compromiso con su estado correcto.
// Las compras y ventas se conservan (solo pierden el vínculo con el dinero).
export async function eliminarMovimiento(mov) {
  const v = await buscarVinculos(mov.id);
  await Promise.all([
    v.pagos.length ? q(supabase.from('pagos_deuda').delete().eq('movimiento_id', mov.id)) : null,
    v.cobros.length ? q(supabase.from('cobros_prestamo').delete().eq('movimiento_id', mov.id)) : null,
    v.abonos.length ? q(supabase.from('abonos').delete().eq('movimiento_id', mov.id)) : null,
  ]);
  await q(supabase.from('bolsillo_movimientos').delete().eq('id', mov.id));
  await Promise.all([
    ...v.pagos.map(p => recalcularDeuda(p.deuda_id)),
    ...v.cobros.map(c => recalcularPrestamo(c.prestamo_id)),
    ...v.abonos.map(a => recalcularCompromiso(a.compromiso_id)),
    mov.gasto_fijo_id ? desmarcarGastoFijo(mov.gasto_fijo_id, mov.fecha) : null,
  ]);
}

// Edita un movimiento y replica monto/fecha en el pago, cobro o abono vinculado.
export async function actualizarMovimiento(mov, cambios) {
  // Si el gasto tenía factura y cambió el monto, su IVA se recalcula solo
  const ajustes = mov.con_factura && cambios.monto != null
    ? { ...cambios, iva: ivaDesdeTotal(cambios.monto) }
    : cambios;
  await q(supabase.from('bolsillo_movimientos').update(ajustes).eq('id', mov.id));
  const sync = { monto: cambios.monto, fecha: cambios.fecha };
  const v = await buscarVinculos(mov.id);
  await Promise.all([
    v.pagos.length ? q(supabase.from('pagos_deuda').update(sync).eq('movimiento_id', mov.id)) : null,
    v.cobros.length ? q(supabase.from('cobros_prestamo').update(sync).eq('movimiento_id', mov.id)) : null,
    v.abonos.length ? q(supabase.from('abonos').update(sync).eq('movimiento_id', mov.id)) : null,
  ]);
  await Promise.all([
    ...v.pagos.map(p => recalcularDeuda(p.deuda_id)),
    ...v.cobros.map(c => recalcularPrestamo(c.prestamo_id)),
    ...v.abonos.map(a => recalcularCompromiso(a.compromiso_id)),
  ]);
}
